import { useEffect, useId, useRef, useState } from 'react'
import './HydrationWeekChart.css'

const EMPTY_WEEK = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((label) => ({
  label,
  liters: 0,
  goal: 0
}))

function niceMax(value) {
  if (value <= 0) return 1
  const padded = value * 1.18
  const step = padded <= 2 ? 0.5 : padded <= 5 ? 1 : 2
  return Math.ceil(padded / step) * step
}

function tickLabel(value) {
  return Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 })
}

function HydrationWeekChart({ days = [] }) {
  const rawId = useId().replace(/:/g, '')
  const goalShadow = `hydration-goal-shadow-${rawId}`
  const intakeShadow = `hydration-intake-shadow-${rawId}`
  const series = days.length ? days : EMPTY_WEEK
  const boxRef = useRef(null)
  const [box, setBox] = useState({ width: 0, height: 0 })
  useEffect(() => {
    const node = boxRef.current
    if (!node) return undefined
    const measure = () => {
      const next = {
        width: Math.round(node.clientWidth),
        height: Math.round(node.clientHeight)
      }
      setBox((current) => (
        current.width === next.width && current.height === next.height ? current : next
      ))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  const width = Math.max(240, box.width || 320)
  const height = Math.max(96, box.height || 116)
  const left = 32
  const right = 8
  const top = 16
  const bottom = 16
  const plotWidth = width - left - right
  const plotHeight = height - top - bottom
  const yMax = niceMax(Math.max(...series.map((day) => Math.max(Number(day.liters) || 0, Number(day.goal) || 0))))
  const xAt = (index) => (
    series.length <= 1 ? left + plotWidth / 2 : left + (index / (series.length - 1)) * plotWidth
  )
  const yAt = (value) => top + plotHeight - ((Number(value) || 0) / yMax) * plotHeight
  const line = (key) => series.map((day, index) => `${xAt(index)},${yAt(day[key])}`).join(' ')
  const ticks = [0, yMax / 2, yMax]

  return (
    <article className="hydration-card hydration-week" aria-label="Tendência de hidratação semanal">
      <header className="hydration-week-head">
        <h2>Tendência de hidratação semanal</h2>
        <div className="hydration-legend">
          <span><i className="is-goal" /> meta</span>
          <span><i className="is-intake" aria-label="água" /></span>
        </div>
      </header>
      <div className="hydration-week-plot" ref={boxRef}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Litros por dia da semana">
        <defs>
          <filter id={goalShadow} x="-15%" y="-40%" width="130%" height="180%">
            <feDropShadow dx="0" dy="2" stdDeviation="1.8" floodColor="#0369a1" floodOpacity="0.45" />
          </filter>
          <filter id={intakeShadow} x="-15%" y="-40%" width="130%" height="180%">
            <feDropShadow dx="0" dy="2" stdDeviation="1.8" floodColor="#38bdf8" floodOpacity="0.7" />
          </filter>
        </defs>
        {ticks.map((tick) => (
          <g key={tick}>
            <line className="hydration-grid" x1={left} y1={yAt(tick)} x2={width - right} y2={yAt(tick)} />
            <text className="hydration-axis" x={left - 6} y={yAt(tick) + 3} textAnchor="end">{tickLabel(tick)}</text>
          </g>
        ))}
        <text className="hydration-axis hydration-axis-unit" x={left - 6} y={9} textAnchor="end">L</text>
        <polyline className="hydration-line is-goal" points={line('goal')} filter={`url(#${goalShadow})`} />
        <polyline className="hydration-line is-intake" points={line('liters')} filter={`url(#${intakeShadow})`} />
        {series.map((day, index) => (
          <g key={`${day.label}-${index}`}>
            <circle className="hydration-point is-goal" cx={xAt(index)} cy={yAt(day.goal)} r="2.4" />
            <circle className="hydration-point is-intake" cx={xAt(index)} cy={yAt(day.liters)} r="2.6" />
            <text className="hydration-axis" x={xAt(index)} y={height - 8} textAnchor="middle">{day.label}</text>
          </g>
        ))}
      </svg>
      </div>
    </article>
  )
}

export default HydrationWeekChart
